import React from "react";
import { Check } from "@phosphor-icons/react";
import { PATHWAY, stageIndexForStatus, fmtAgo } from "./format";

/**
 * 7-stage Clinical Pathway progress bar.
 *
 * iter101.11 · Phase 1 of the Universal Case Page migration — softened
 * from the previous bulky number-circle stepper (w-9 h-9 rings, heavy
 * shadow on active) into a dot-progress bar that matches the doctor
 * portal's clinical-pathway visual weight. Stage granularity is
 * preserved (7 admin-side stages: Submission → Admin Review → Treatment
 * Planning → Doctor Approval → Production → QC & Packaging → Shipped)
 * because it stays useful for admin's routing decisions; only the paint
 * is different.
 *
 *   ●────●────●────●────○────○────○
 *   Sub  Admin Plan Appr Prod QC   Ship
 */
export function ClinicalPathwayStepper({ c }) {
    const currentIdx = stageIndexForStatus(c?.status);

    // Per-stage owner / timestamp — rendered small under the label.
    const meta = {
        0: c?.doctor_name && `${c.doctor_name}`,
        1: c?.timeline?.find?.((e) => e.action === "planner_assigned")?.author_name || null,
        2: c?.planner_name || null,
        3: c?.status === "approved" && c?.status_updated_at ? fmtAgo(c.status_updated_at) : null,
        4: c?.production_tech_name || null,
        5: c?.qa_name || null,
        6: c?.timeline?.find?.((e) => e.action === "shipped")?.at
            ? fmtAgo(c.timeline.find((e) => e.action === "shipped").at)
            : null,
    };

    return (
        <div className="mt-4 bg-card border border-border rounded-2xl px-4 py-3 sm:px-5 sm:py-4" data-testid="case-pathway-stepper">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground mb-3">
                Clinical Pathway
            </div>
            <div className="flex items-start justify-between gap-1">
                {PATHWAY.map((stage, idx) => {
                    const done = idx < currentIdx;
                    const active = idx === currentIdx;
                    const isLast = idx === PATHWAY.length - 1;

                    // Softer palette: primary teal for active, deeper teal
                    // for done, muted for upcoming.
                    const dotCls = active
                        ? "bg-primary border-primary ring-4 ring-primary/15"
                        : done
                          ? "bg-primary border-primary"
                          : "bg-card border-border";
                    const labelCls = active
                        ? "text-primary font-semibold"
                        : done
                          ? "text-primary/80"
                          : "text-muted-foreground";
                    const connectorCls = done ? "bg-primary" : "bg-border";

                    return (
                        <React.Fragment key={stage.key}>
                            <div
                                className="flex-1 flex flex-col items-center min-w-0"
                                data-testid={`pathway-stage-${stage.key}${active ? "-active" : done ? "-done" : ""}`}
                            >
                                <div className="flex items-center w-full">
                                    {/* Left connector (hidden on first) */}
                                    <div className={`flex-1 h-px ${idx === 0 ? "invisible" : done ? "bg-primary" : "bg-border"}`} />
                                    {/* Dot */}
                                    <div
                                        className={`w-3 h-3 rounded-full border-2 shrink-0 transition-all flex items-center justify-center ${dotCls}`}
                                        title={stage.label}
                                    >
                                        {done && <Check size={8} weight="bold" className="text-white" />}
                                    </div>
                                    {/* Right connector (hidden on last) */}
                                    <div className={`flex-1 h-px ${isLast ? "invisible" : connectorCls}`} />
                                </div>
                                <div className="mt-2 text-center max-w-full px-0.5">
                                    <div className={`text-[10px] sm:text-[11px] leading-tight truncate ${labelCls}`}>
                                        {stage.label}
                                    </div>
                                    {meta[idx] && (
                                        <div className="text-[9px] text-muted-foreground/70 truncate mt-0.5 leading-tight hidden md:block">
                                            {meta[idx]}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}
